import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { inventoryEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * Input type for archiving inventory items
 */
export interface ArchiveInventoryInput {
	servicesProviderId: string;
	inventoryIds: string[];
	archiveReason: string;
}

/**
 * Response type for archive inventory operation
 */
export interface ArchiveInventoryResponse {
	archived: number;
	total: number;
}

/**
 * API function to archive inventory items
 */
export const archiveInventory = async ({
	servicesProviderId,
	inventoryIds,
	archiveReason,
}: ArchiveInventoryInput): Promise<ArchiveInventoryResponse> => {
	const response = await apiClient.post<ArchiveInventoryResponse>(
		inventoryEndpoints.archive(servicesProviderId),
		{ inventoryIds, archiveReason },
	);
	return response.data;
};

/**
 * Hook for archiving inventory items
 */
export const useArchiveInventoryMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: archiveInventory,
		onSuccess: () => {
			// Invalidate inventory list to refresh data
			// Invalidate the inventory list and detail queries to ensure they get fresh data
			queryClient.invalidateQueries({
				queryKey: queries.inventory.list._def,
			});
			queryClient.invalidateQueries({
				queryKey: queries.inventory.detail._def,
			});
		},
	});
};

/**
 * Hook for archiving inventory with services provider context
 */
export const useArchiveInventory = () => {
	const mutation = useArchiveInventoryMutation();
	const currentServicesProviderId = useCurrentServicesProviderId();

	const handleArchiveInventory = async (inventoryIds: string[], archiveReason: string) => {
		if (!currentServicesProviderId) {
			throw new Error('No services provider ID available');
		}

		return mutation.mutateAsync({
			servicesProviderId: currentServicesProviderId,
			inventoryIds,
			archiveReason,
		});
	};

	return {
		archiveInventory: handleArchiveInventory,
		isArchiving: mutation.isPending,
	};
};
